import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Building2, Plus, Edit2, Trash2, Save, Lock } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const formatError = (err) => {
  const d = err?.response?.data?.detail;
  if (typeof d === 'string') return d;
  if (Array.isArray(d)) return d.map(e => e.msg || JSON.stringify(e)).join(', ');
  if (d && typeof d === 'object') return d.msg || JSON.stringify(d);
  return err?.message || 'Error';
};

const emptyForm = { name: '', color: '#6A736B', order: 100 };

export function PavilionsCard() {
  const [pavilions, setPavilions] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const fetchAll = async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/pavilions`, { withCredentials: true });
      setPavilions(data);
    } catch (e) {
      console.error('Error fetching pavilions:', e);
    }
  };

  useEffect(() => { fetchAll(); }, []);

  const openCreate = () => {
    setEditing(null);
    setForm({ ...emptyForm, order: (pavilions.length + 1) });
    setError('');
    setDialogOpen(true);
  };

  const openEdit = (p) => {
    setEditing(p);
    setForm({ name: p.name, color: p.color || '#6A736B', order: p.order ?? 100 });
    setError('');
    setDialogOpen(true);
  };

  const handleSave = async () => {
    setSaving(true);
    setError('');
    try {
      if (editing) {
        await axios.put(`${API_URL}/api/pavilions/${editing.id}`, {
          name: form.name,
          color: form.color,
          order: form.order,
        }, { withCredentials: true });
      } else {
        await axios.post(`${API_URL}/api/pavilions`, form, { withCredentials: true });
      }
      setDialogOpen(false);
      fetchAll();
    } catch (e) {
      setError(formatError(e));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (p) => {
    if (!window.confirm(`¿Eliminar el pabellón "${p.name}"?`)) return;
    try {
      await axios.delete(`${API_URL}/api/pavilions/${p.id}`, { withCredentials: true });
      fetchAll();
    } catch (e) {
      alert(formatError(e));
    }
  };

  return (
    <Card className="bg-white rounded-2xl border border-[#E8E5DD]" data-testid="pavilions-card">
      <CardHeader className="pb-3 flex flex-row items-center justify-between gap-3">
        <CardTitle className="flex items-center gap-2 text-lg font-semibold text-[#1A1C18]">
          <Building2 size={20} className="text-[#2B4C3B]" />
          Pabellones
        </CardTitle>
        <Button
          data-testid="new-pavilion-btn"
          onClick={openCreate}
          className="bg-[#2B4C3B] hover:bg-[#1F3A2C] text-white h-9 px-3"
        >
          <Plus size={16} className="mr-2" /> Nuevo Pabellón
        </Button>
      </CardHeader>
      <CardContent>
        <div className="space-y-2">
          {pavilions.map((p) => (
            <div
              key={p.id}
              data-testid={`pavilion-row-${p.name}`}
              className="flex items-center justify-between gap-3 p-3 border border-[#E8E5DD] rounded-lg hover:bg-[#F9F7F2]"
            >
              <div className="flex items-center gap-3 flex-1 min-w-0">
                <div
                  className="w-4 h-4 rounded-full border border-white shadow-sm flex-shrink-0"
                  style={{ backgroundColor: p.color }}
                />
                <div className="flex items-center gap-2 flex-1 min-w-0">
                  <span className="font-medium text-[#1A1C18] truncate">{p.name}</span>
                  {p.is_default && (
                    <span className="inline-flex items-center text-[10px] text-[#6A736B] bg-[#F3EBE1] rounded-full px-2 py-0.5">
                      <Lock size={10} className="mr-1" /> Default
                    </span>
                  )}
                </div>
                <span className="text-[11px] font-mono text-[#6A736B] hidden sm:inline">{p.color}</span>
              </div>
              <div className="flex items-center gap-1">
                <Button
                  data-testid={`edit-pavilion-${p.name}`}
                  variant="ghost" size="sm" onClick={() => openEdit(p)}
                  className="text-[#2B4C3B] hover:bg-[#F3EBE1] h-8 w-8 p-0"
                >
                  <Edit2 size={14} />
                </Button>
                <Button
                  data-testid={`delete-pavilion-${p.name}`}
                  variant="ghost" size="sm" onClick={() => handleDelete(p)}
                  className="text-[#A65C5C] hover:bg-red-50 h-8 w-8 p-0"
                >
                  <Trash2 size={14} />
                </Button>
              </div>
            </div>
          ))}
          {pavilions.length === 0 && (
            <p className="text-sm text-[#6A736B] text-center py-6">No hay pabellones creados</p>
          )}
        </div>
      </CardContent>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="bg-white border-[#E8E5DD] sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-xl text-[#1A1C18]">
              {editing ? `Editar: ${editing.name}` : 'Nuevo Pabellón'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Nombre</Label>
              <Input
                data-testid="pavilion-name-input"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="Ej: Pabellón FMSC"
                className="mt-1"
              />
            </div>
            <div>
              <Label>Color</Label>
              <div className="flex items-center gap-2 mt-1">
                <input
                  type="color"
                  data-testid="pavilion-color-input"
                  value={form.color}
                  onChange={(e) => setForm({ ...form, color: e.target.value })}
                  className="w-12 h-10 rounded cursor-pointer border border-[#E8E5DD]"
                />
                <Input
                  value={form.color}
                  onChange={(e) => setForm({ ...form, color: e.target.value })}
                  className="font-mono text-xs"
                />
              </div>
              <div className="flex gap-1.5 flex-wrap mt-2">
                {['#0B8043', '#D93025', '#7C3AED', '#F59E0B', '#1A73E8', '#EC4899', '#0EA5E9', '#84CC16', '#6B7280'].map(c => (
                  <button
                    key={c}
                    type="button"
                    onClick={() => setForm({ ...form, color: c })}
                    className={`w-7 h-7 rounded-full border-2 transition-transform hover:scale-110 ${form.color === c ? 'border-[#2B4C3B] scale-110' : 'border-transparent'}`}
                    style={{ backgroundColor: c }}
                  />
                ))}
              </div>
            </div>
            <div>
              <Label>Orden (menor aparece primero)</Label>
              <Input
                type="number"
                value={form.order}
                onChange={(e) => setForm({ ...form, order: parseInt(e.target.value || '0', 10) })}
                className="mt-1"
              />
            </div>
            {error && (
              <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
                <p className="text-sm text-red-800">{error}</p>
              </div>
            )}
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setDialogOpen(false)} disabled={saving}>
                Cancelar
              </Button>
              <Button
                data-testid="save-pavilion-btn"
                onClick={handleSave}
                disabled={saving || !form.name.trim()}
                className="bg-[#2B4C3B] hover:bg-[#1F3A2C] text-white"
              >
                <Save size={16} className="mr-2" />
                {saving ? 'Guardando…' : (editing ? 'Actualizar' : 'Crear')}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </Card>
  );
}
